import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { ExportDialog } from '../components/ExportDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  UserCheck, Loader2, Filter, FileSpreadsheet,
  Link2, Printer, FileText, Coins,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AngariadoresDashboard = () => {
  const { isFullAccess } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [partners, setPartners] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showExport, setShowExport] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchPartners = async () => {
    try {
      const res = await axios.get(`${API_URL}/partners`);
      setPartners(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchData = async (pf) => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const pv = pf !== undefined ? pf : partnerFilter;
      if (pv) params.partner_id = pv;
      const res = await axios.get(`${API_URL}/dashboard/angariadores`, { params });
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
    fetchData();
  }, []);

  const handleFilter = () => fetchData();
  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    setPartnerFilter('');
    setTimeout(() => fetchData(''), 0);
  };

  const handlePartnerChange = (val) => {
    const v = val === 'all' ? '' : val;
    setPartnerFilter(v);
    fetchData(v);
  };

  const angariadores = data?.angariadores || [];
  const totals = data?.totals || {};

  const toggleId = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleAll = () => {
    setSelectedIds(prev =>
      prev.length === angariadores.length ? [] : angariadores.map(a => a.user_id)
    );
  };

  const handleExport = async (columns) => {
    const ids = selectedIds.length > 0 ? selectedIds : angariadores.map(a => a.user_id);
    if (ids.length === 0) { toast.error('Sem angariadores para exportar'); return; }
    setExporting(true);
    try {
      const res = await axios.post(`${API_URL}/production/export-excel`, {
        type: 'angariadores',
        entity_ids: ids,
        columns,
        start_date: startDate || null,
        end_date: endDate || null,
      }, { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const disp = res.headers['content-disposition'];
      const fname = disp ? disp.split('filename="')[1]?.replace('"', '') : 'producao_angariadores.xlsx';
      link.setAttribute('download', fname);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exportado');
      setShowExport(false);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao exportar');
    } finally {
      setExporting(false);
    }
  };

  if (loading && !data) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="angariadores-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
              <UserCheck className="w-6 h-6 text-[#2D3E6F]" />
              Producao de Angariadores
            </h1>
            <p className="text-slate-500 mt-1">
              {totals.count || 0} angariadores
              {partnerFilter ? ` - ${partners.find(p => p.id === partnerFilter)?.name || ''}` : ''}
              {startDate || endDate ? ' (filtrado)' : ''}
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => window.print()} variant="outline" className="gap-2 print:hidden" data-testid="print-angariadores-btn">
              <Printer className="w-4 h-4" /> Imprimir
            </Button>
            <Button
              onClick={() => setShowExport(true)}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-full gap-2"
              data-testid="export-angariadores-excel-btn"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Exportar Excel {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="rounded-2xl border-slate-100">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row items-end gap-4">
              {isFullAccess() && (
                <div className="flex-1">
                  <Label className="text-xs text-slate-500">Parceiro</Label>
                  <Select value={partnerFilter || 'all'} onValueChange={handlePartnerChange}>
                    <SelectTrigger className="h-10 rounded-lg" data-testid="ang-filter-partner">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os parceiros</SelectItem>
                      <SelectItem value="__internal__">Equipa Interna</SelectItem>
                      {partners.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Data Inicio</Label>
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-10 rounded-lg" data-testid="ang-filter-start" />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Data Fim</Label>
                <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-10 rounded-lg" data-testid="ang-filter-end" />
              </div>
              <Button onClick={handleFilter} className="bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2 h-10" data-testid="ang-filter-apply">
                <Filter className="w-4 h-4" /> Filtrar
              </Button>
              {(startDate || endDate || partnerFilter) && (
                <Button onClick={handleClearFilter} variant="outline" className="h-10" data-testid="ang-filter-clear">Limpar</Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-[#2D3E6F]">{totals.count || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Angariadores</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-emerald-600">{totals.total_leads || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Leads Geradas</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-600">{totals.total_contracts_from_leads || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Contratos (Leads)</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-600">{totals.total_own_contracts || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Contratos Proprios</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-slate-700">{totals.total_all_contracts || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Total Contratos</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-600">{(totals.total_commission || 0).toFixed(2)}</p>
              <p className="text-xs text-slate-500 mt-1">Comissao Total</p>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card className="rounded-2xl border-slate-100 overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-[#2D3E6F]" />
              Angariadores
            </CardTitle>
          </CardHeader>
          <div className="overflow-x-auto">
            <Table className="min-w-[1100px]">
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="w-10">
                    <Checkbox
                      checked={selectedIds.length === angariadores.length && angariadores.length > 0}
                      onCheckedChange={toggleAll}
                      data-testid="select-all-angariadores"
                    />
                  </TableHead>
                  <TableHead className="font-semibold w-10">#</TableHead>
                  <TableHead className="font-semibold">Angariador</TableHead>
                  <TableHead className="font-semibold">Comercial</TableHead>
                  <TableHead className="font-semibold">Parceiro</TableHead>
                  <TableHead className="font-semibold text-center">Leads</TableHead>
                  <TableHead className="font-semibold text-center">Contr. Leads</TableHead>
                  <TableHead className="font-semibold text-center">Contr. Proprios</TableHead>
                  <TableHead className="font-semibold text-center">Concluidos</TableHead>
                  <TableHead className="font-semibold text-center">Pendentes</TableHead>
                  <TableHead className="font-semibold text-center">Anulados</TableHead>
                  <TableHead className="font-semibold text-right">Comissao</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {angariadores.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-12 text-slate-400">
                      Sem angariadores encontrados
                    </TableCell>
                  </TableRow>
                ) : (
                  angariadores.map((a, i) => (
                    <TableRow
                      key={a.user_id}
                      className={`hover:bg-slate-50/50 ${selectedIds.includes(a.user_id) ? 'bg-blue-50/50' : ''}`}
                      data-testid={`angariador-row-${i}`}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.includes(a.user_id)}
                          onCheckedChange={() => toggleId(a.user_id)}
                        />
                      </TableCell>
                      <TableCell className="font-bold text-[#2D3E6F]">{i + 1}</TableCell>
                      <TableCell>
                        <div>
                          <span className="font-medium">{a.user_name}</span>
                          {a.email && <span className="block text-xs text-slate-400">{a.email}</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-medium text-[#2D3E6F]">{a.comercial_name || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">{a.partner_name || '-'}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                          <Link2 className="w-3.5 h-3.5" /> {a.leads_count}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center gap-1 font-medium text-blue-600">
                          <FileText className="w-3.5 h-3.5" /> {a.contracts_from_leads || 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-sm font-medium text-slate-700">{a.own_contracts || 0}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                          {a.by_status?.concluido || 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          {a.by_status?.pendente || 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          {a.by_status?.anulado || 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-medium text-purple-700">
                          <Coins className="w-3.5 h-3.5 inline mr-1" />
                          {(a.total_commission || 0).toFixed(2)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>

      <ExportDialog
        open={showExport}
        onClose={setShowExport}
        onExport={handleExport}
        exporting={exporting}
        title={`Exportar Angariadores ${selectedIds.length > 0 ? `(${selectedIds.length})` : '(Todos)'}`}
      />
    </Layout>
  );
};
